import React, { useEffect, useState } from 'react';
import '../PricingCard/styles.css';
import { getPlans } from '@custom/rtk-apis/Setting/plans/getPlans';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { useTheme } from "../../../ui/src/contextProviders"
import ToggleSwitch from '../PricingCard/ToggleSwitch';
import { CheckIcon } from '../PricingCard/icons';
import sendPayementLink from '@custom/rtk-apis/subscription/sendPayementLink';

interface Plan {
  name: string;
  monthlyUsers: string;
  yearlyUsers: string;
  monthlyCloudStorage: string;
  yearlyCloudStorage: string;
  monthlySpeechToText?: boolean;
  monthlyAiServices?: boolean;
  yearlySpeechToText?: boolean;
  yearlyAiServices?: boolean;
  monthlyPrice: number;
  yearlyPrice: number;
  customMonthlyFeatures: string;
  customYearlyFeatures: string;
  plan_code: string
}

interface PricingCardProps {
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  showModal?: boolean;
  selectedHospital?: any;
  setShowModal?: Function;
}

const PricingCardSuperAdmin: React.FC<PricingCardProps> = ({ loading, setLoading, showModal = false, selectedHospital, setShowModal }) => {
  const dispatch = useDispatch<AppDispatch>();
  const plansData = useSelector((state: RootState) => state?.getPlansResponse);
  const [isMonthly, setIsMonthly] = useState(true);
  const [rawPlans, setRawPlans] = useState([]);

  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPurchasedPlan, setCurrentPurchasedPlan] = useState<string>('');
  const { isDarkMode } = useTheme();

  useEffect(() => {
    dispatch(getPlans());
  }, [dispatch]);

  useEffect(() => {
    if (plansData?.success) {
      // @ts-ignore
      setRawPlans(plansData.plans.data);
      const mappedPlans = plansData?.plans?.data?.map((plan: any) => ({
        name: plan.name,
        monthlyUsers: plan.monthly?.users,
        yearlyUsers: plan.yearly?.users,
        monthlyCloudStorage: plan.monthly?.cloudStorage,
        yearlyCloudStorage: plan.yearly?.cloudStorage,
        monthlySpeechToText: plan.monthly?.speechToText,
        monthlyAiServices: plan.monthly?.aiServices,
        yearlySpeechToText: plan.yearly?.speechToText,
        yearlyAiServices: plan.yearly?.aiServices,
        monthlyPrice: plan.monthly?.planPrice,
        yearlyPrice: plan.yearly?.planPrice,
        customMonthlyFeatures: plan.monthly?.customFeatures || [],
        customYearlyFeatures: plan.yearly?.customFeatures || [],
        plan_code: plan.plan_code
      })) || [];
      setPlans(mappedPlans);
    }
  }, [plansData?.success]);

  const togglePricingPeriod = () => {
    setIsMonthly(!isMonthly);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    if (setLoading) {
      setLoading(false);
    }
  };

  const openCheckout = (plan:Plan) => {
    setLoading(true);
    const selectedPlan = rawPlans.find((p: any) => p.plan_code === plan.plan_code);
    const payload = {
      planDetails: selectedPlan,
      hospital_code: selectedHospital?.hospitalCode,
      mailid: selectedHospital?.user?.email,
    }
    dispatch(sendPayementLink(payload)).then((response:any)=>{
      setLoading(false);
    }).finally(()=>{
      window.location.reload()
    })
  }

  return (
    <>
      <div className={`pricing-modal-overlay ${showModal ? 'open' : ''}`} onClick={closeModal}>
        <div
          className={`pricing-modal-content ${isDarkMode ? 'dark-mode' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`pricing-modal-header ${isDarkMode ? 'dark-mode' : ''}`}>
            <h2 className={isDarkMode ? 'text-white' : ''}>Choose Your Plan</h2>
            <button className={`close-button ${isDarkMode ? 'dark-mode' : ''}`} onClick={closeModal}>×</button>
          </div>

          <div className="toggle-container">
            <ToggleSwitch
              isMonthly={isMonthly}
              onToggle={togglePricingPeriod}
            />
          </div>

          <div className="hospital-pricing-cards-container">
            {plans.map((plan, index) => (
              <div
                className={`pricing-card ${plan.name === 'Professional' ? 'popular' : ''} ${isDarkMode ? 'dark-mode' : ''}`}
                key={index}
              >
                <div className={`pricing-card-header ${isDarkMode ? 'dark-mode' : ''}`}>
                  {/* {plan.name === 'Professional' && <div className="popular-badge">Most Popular</div>} */}
                  <h3 className={isDarkMode ? 'text-white' : ''}>{plan.name}</h3>
                  {isMonthly ? (
                    <p className="price">₹{plan.monthlyPrice}</p>
                  ) : (
                    <p className="price">₹{plan.yearlyPrice}</p>
                  )}
                </div>

                <div className={`pricing-card-body ${isDarkMode ? 'dark-mode' : ''}`}>
                  <ul className="features-list">
                    <li className="feature-item">
                      <CheckIcon />
                      <span className={isDarkMode ? 'text-white' : ''}>
                        {isMonthly ? plan.monthlyUsers : plan.yearlyUsers} Users
                      </span>
                    </li>
                    {isMonthly ? plan.monthlySpeechToText && (
                      <li className="feature-item">
                        <CheckIcon />
                        <span className={isDarkMode ? 'text-white' : ''}>Speech to Text</span>
                      </li>
                    ) : plan.yearlySpeechToText && (
                      <li className="feature-item">
                        <CheckIcon />
                        <span className={isDarkMode ? 'text-white' : ''}>Speech to Text</span>
                      </li>
                    )}
                    {isMonthly ? plan.monthlyAiServices && (
                      <li className="feature-item">
                        <CheckIcon />
                        <span className={isDarkMode ? 'text-white' : ''}>AI Services</span>
                      </li>
                    ) : plan.yearlyAiServices && (
                      <li className="feature-item">
                        <CheckIcon />
                        <span className={isDarkMode ? 'text-white' : ''}>AI Services</span>
                      </li>
                    )}
                    <li className="feature-item">
                      <CheckIcon />
                      <span className={isDarkMode ? 'text-white' : ''}>
                        {isMonthly ? plan.monthlyCloudStorage : plan.yearlyCloudStorage} GB Cloud Storage
                      </span>
                    </li>
                    {isMonthly ? (
                      Array.isArray(plan.customMonthlyFeatures) && plan.customMonthlyFeatures.length > 0 && (
                        plan.customMonthlyFeatures.map((feature, index) => (
                          <li key={index} className="feature-item">
                            <CheckIcon />
                            <span className={isDarkMode ? 'text-white' : ''}>{feature}</span>
                          </li>
                        ))
                      )
                    ) : (
                      Array.isArray(plan.customYearlyFeatures) && plan.customYearlyFeatures.length > 0 && (
                        plan.customYearlyFeatures.map((feature, index) => (
                          <li key={index} className="feature-item">
                            <CheckIcon />
                            <span className={isDarkMode ? 'text-white' : ''}>{feature}</span>
                          </li>
                        ))
                      )
                    )}
                  </ul>
                </div>

                <div className={`pricing-card-footer ${isDarkMode ? 'dark-mode' : ''}`}>
                  <button disabled={loading || currentPurchasedPlan === plan.plan_code} className="select-plan-btn" onClick={() => openCheckout(plan)}>
                    {currentPurchasedPlan === plan.plan_code ? 'Current Plan'  : `Select Plan`}
                  </button>
                </div>
              </div>
            ))}

          </div>
        </div>
      </div>
    </>
  );
};

export default PricingCardSuperAdmin;
